import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { Plural, t } from '@lingui/macro';

import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';

import { pad00 } from '$/utils/string';
import EDate from '$/shared/frontend/utils/EDate';
import { useServerContext } from '$/hooks/useServerDateNow';

export const CountdownTimer: React.FC<{
  label?: string;
  isRtl: boolean;
  expiredLabel?: string;
  className?: string;
  date: EDate;
  fontEM?: number;
  onExpired?: () => void;
}> = withWebinarPlayerContext(
  ['dimensions'],
  'CountdownTimer'
)(({ label, isRtl, expiredLabel = t`NOW`, className, date, fontEM, onExpired, context }) => {
  const { dimensions } = context;
  const { serverDateNow } = useServerContext();
  fontEM = fontEM ?? (dimensions.videoArea.width >= 768 ? 3 : 2);

  const [timeLeftSecs, setTimeLeftSecs] = React.useState(0);
  React.useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    const updateTimer = (): number => {
      const secs = date.diffInSeconds(new EDate(serverDateNow(), 'UTC'));
      setTimeLeftSecs(secs);

      if (secs <= 0) {
        if (timer) {
          clearInterval(timer);
          timer = undefined;
        }

        onExpired && onExpired();
      }

      return secs;
    };

    const secs = updateTimer();
    if (secs > 0) {
      timer = setInterval(updateTimer, 1000);
    }
    return (): void => {
      if (timer) clearInterval(timer);
    };
  }, [date, onExpired, serverDateNow]);

  if (timeLeftSecs <= 0) {
    return <div>{expiredLabel}</div>;
  }

  let secondsFromNow = timeLeftSecs;

  const daysFromNow = Math.floor(secondsFromNow / (24 * 60 * 60));
  secondsFromNow = secondsFromNow % (24 * 60 * 60);

  const hoursFromNow = Math.floor(secondsFromNow / (60 * 60));
  secondsFromNow = secondsFromNow % (60 * 60);

  const minutesFromNow = Math.floor(secondsFromNow / 60);
  secondsFromNow = Math.floor(secondsFromNow % 60);

  const fontSM = fontEM * 0.4;
  return (
    <div
      id='session'
      className={`flex flex-col items-start ${className}`}
      css={[
        tw`font-semibold`,
        css`
          font-size: ${fontSM}rem;
          white-space: nowrap;
          margin-bottom: 0.5rem;
          > div {
            margin-top: ${label ? '0' : '0.25rem'};
          }
          align-items: center;
          text-align: -webkit-right;
          height: initial;
        `,
      ]}
    >
      {label}
      <div
        className='inline-flex items-baseline'
        css={css`
          font-size: ${fontEM}rem;
          margin: 0;
          line-height: 2rem;
          & > span {
            font-size: ${fontSM}rem;
            ${isRtl ? 'margin-left' : 'margin-right'}: 0.5rem;
          }
        `}
      >
        {Boolean(daysFromNow) && (
          <>
            {daysFromNow}{' '}
            <span>
              <Plural value={daysFromNow} one='day' other='days' />
            </span>
          </>
        )}
        {Boolean(daysFromNow || hoursFromNow) && (
          <>
            {hoursFromNow}{' '}
            <span>
              <Plural value={hoursFromNow} one='hour' other='hours' />
            </span>
          </>
        )}
        {pad00(minutesFromNow)}{' '}
        <span>
          <Plural value={minutesFromNow} one='minute' other='minutes' />
        </span>
        <div>{pad00(secondsFromNow)}</div>{' '}
        <span>
          <Plural value={secondsFromNow} one='second' other='seconds' />
        </span>
      </div>
    </div>
  );
});
